import { CraNodeCoding } from "../../coding/models/CraNodeCoding";
import { CraWaySnapshot } from "../../coding/models/CraWaySnapshot";
import { CraComment } from "../../coding/models/CraComment";
import {
  jwtAuthAxios,
  optionalAuthAxios,
} from "../../shared/utils/network-utils";
import { OptionalScope } from "../../shared/models/OptionalScope";
import { LifeCycleStage } from "../models/LifeCycleStage";
import {
  CreateCraWay,
  CraWay,
  UpdateCraWay,
  DeviceOutputLinksResponse,
  GpxUploadLinksResponse,
  UploadInputType,
  UploadLinksRequest,
} from "../models/CraWay";
import { MobileAppPhotoProcessing } from "../models/MobileAppPhotoProcessing";
import {
  CraWayUploadStatus,
  CraNode,
  UpdateCraNodeLocation,
} from "../models/CraNode";
import { compileAllPages, Page } from "../../shared/utils/http-utils";
import { CopyNodesRequest } from "../models/CopyNodesRequest";
import { LatestCodingChange } from "../../coding/models/LatestCodingChange";

const prefix = "/cra-ways";

export const CraWayApi = {
  get: async (craWayId: string, scopes: OptionalScope[] = []) =>
    await optionalAuthAxios().get<CraWay>(`${prefix}/${craWayId}`, {
      params: { scopes: scopes.join() },
    }),

  pageNodes: async (
    craWayId: string,
    scopes: OptionalScope[],
    pageSize: number,
    pageNumber: number,
  ) =>
    await optionalAuthAxios().get<Page<CraNode>>(
      `${prefix}/${craWayId}/cra-nodes`,
      {
        params: {
          "page[size]": pageSize,
          "page[number]": pageNumber,
          scopes: scopes.join(),
        },
      },
    ),

  compileAllNodePages: async (
    craWayId: string,
    scopes: OptionalScope[],
    pageSize: number,
  ) =>
    await compileAllPages<CraNode>(
      async (pageNumber) =>
        await CraWayApi.pageNodes(craWayId, scopes, pageSize, pageNumber),
    ),

  listCraWaySnapshots: async (craWayId: string) =>
    await jwtAuthAxios().get<CraWaySnapshot[]>(
      `${prefix}/${craWayId}/all-snapshots`,
    ),

  create: async (request: CreateCraWay, scopes: OptionalScope[] = []) =>
    await jwtAuthAxios().post<CraWay>(`${prefix}?scopes=${scopes}`, request),

  update: async (
    craWayId: string,
    way: UpdateCraWay,
    scopes: OptionalScope[],
  ) =>
    await jwtAuthAxios().put<CraWay>(
      `${prefix}/${craWayId}?scopes=${scopes.join()}`,
      way,
    ),

  updateLifeCycleStage: async (
    craWayId: string,
    lifeCycleStage: LifeCycleStage,
    scopes: OptionalScope[] = [],
  ) =>
    await jwtAuthAxios().put<CraWay>(
      `${prefix}/${craWayId}/life-cycle-stage?scopes=${scopes}`,
      {
        lifeCycleStage,
      },
    ),

  analyzeCoding: async (craWayId: string) =>
    await jwtAuthAxios().post<void>(`${prefix}/${craWayId}/analyze-coding`),

  getZipUploadLinks: async (craWayId: string, body: UploadLinksRequest) =>
    await jwtAuthAxios().post<
      DeviceOutputLinksResponse | GpxUploadLinksResponse
    >(`${prefix}/${craWayId}/upload-links`, body),

  delete: async (craWayId: string) => {
    return await jwtAuthAxios().delete(`${prefix}/${craWayId}`);
  },

  listCodingEntries: async (craWayId: string) =>
    await optionalAuthAxios().get<CraNodeCoding[]>(
      `${prefix}/${craWayId}/all-coding`,
    ),

  getCodingCsv: async (craWayId: string) => {
    return await jwtAuthAxios().get<Blob>(
      `${prefix}/csv?craWayIds=${craWayId}`,
      {
        responseType: "blob",
      },
    );
  },

  listComments: async (craWayId: string) => {
    return await jwtAuthAxios().get<CraComment[]>(
      `${prefix}/${craWayId}/all-comments`,
    );
  },

  getPhotoUploadStatus: async (craWayId: string) =>
    await jwtAuthAxios().get<MobileAppPhotoProcessing>(
      `${prefix}/${craWayId}/photo-upload-status`,
    ),

  batchUpdateLocation: async (
    craWayId: string,
    nodeLocationUpdate: UpdateCraNodeLocation[],
  ) =>
    await jwtAuthAxios().put(
      `${prefix}/${craWayId}/batch-update-location`,
      nodeLocationUpdate,
    ),

  startReadingUploadedFiles: async (
    craWayId: string,
    inputType: UploadInputType,
  ) => {
    const url = `${prefix}/${craWayId}/read-uploaded-files?inputType=${inputType}`;
    return await jwtAuthAxios().post<CraWayUploadStatus>(url);
  },

  getUploadFilesStatus: async (craWayId: string) => {
    const url = `${prefix}/${craWayId}/read-uploaded-files/status`;
    return await jwtAuthAxios().get<CraWayUploadStatus>(url);
  },

  compileNodes: async (craWayId: string, scopes: OptionalScope[] = []) =>
    await jwtAuthAxios().post<CraWay>(
      `${prefix}/${craWayId}/compile-nodes?scopes=${scopes.join()}`,
    ),

  copyNodes: async (craWayId: string, request: CopyNodesRequest) =>
    await jwtAuthAxios().post<string>(
      `${prefix}/${craWayId}/copy-nodes`,
      request,
    ),

  getLatestCodingChange: async (craWayId: string) =>
    await jwtAuthAxios().get<LatestCodingChange>(
      `${prefix}/${craWayId}/latest-coding-change`,
    ),
};
